import { useState } from 'react';
import { ChevronDown, ChevronUp, Sparkles, Eye, Brain, Sliders, ToggleLeft, ToggleRight } from 'lucide-react';
import { ModelSelectorProps, ModelInfo, LinkedParameter, FeatureToggleProps } from './types';
import { ModelSelectorModal } from './ModelSelectorModal';
import { useI18n } from '../../i18n';

// USD to RMB exchange rate
const USD_TO_RMB = 7.2;

interface EnhancedModelSelectorProps extends Omit<ModelSelectorProps, 'label'> {
  /** Primary label (e.g., "主对话模型") */
  label: string;
  /** Secondary/helper label */
  sublabel?: string;
  /** Generic linked parameters to show in expandable section */
  linkedParameters?: LinkedParameter[];
  /** Callback when any linked parameter changes */
  onParameterChange?: (name: string, value: any) => void;
  /** Whether this is the primary/main model */
  isPrimary?: boolean;
  /** Feature toggle props for optional features */
  featureToggle?: FeatureToggleProps;
  /** Force inline/minimal mode - just a clickable row, no card */
  inline?: boolean;
}

/**
 * Button group component for select-type parameters
 */
function ButtonGroup({
  options,
  value,
  onChange,
  size = 'sm',
}: {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  size?: 'sm' | 'xs';
}) {
  const sizeClasses = size === 'xs'
    ? 'px-2 py-0.5 text-xs'
    : 'px-2.5 py-1 text-xs';

  return (
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`
            ${sizeClasses} rounded-md font-medium transition-all
            ${value === opt.value
              ? 'bg-blue-500 text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Render a single linked parameter control
 */
function ParameterControl({
  param,
  onChange,
}: {
  param: LinkedParameter;
  onChange: (value: any) => void;
}) {
  const { language } = useI18n();

  if (param.type === 'button_group' && param.options) {
    return (
      <div className="space-y-1.5">
        <label className="text-xs text-gray-500">{param.label}</label>
        <ButtonGroup
          options={param.options}
          value={param.value ?? ''}
          onChange={onChange}
        />
        {param.help && (
          <p className="text-xs text-gray-400 mt-1">{param.help}</p>
        )}
      </div>
    );
  }

  if (param.type === 'select' && param.options) {
    return (
      <div className="flex items-center gap-3">
        <label className="w-24 text-xs text-gray-500 flex-shrink-0">
          {param.label}
        </label>
        <select
          value={param.value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded bg-white"
        >
          {param.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (param.type === 'number') {
    const hasRange = param.min !== undefined && param.max !== undefined && param.max <= 2;

    if (hasRange) {
      // Slider + number input for small ranges (like temperature 0-2)
      return (
        <div className="flex items-center gap-3">
          <label className="w-24 text-xs text-gray-500 flex-shrink-0">
            {param.label}
          </label>
          <input
            type="range"
            min={param.min}
            max={param.max}
            step={param.step ?? 0.1}
            value={param.value ?? param.min ?? 0}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <input
            type="number"
            min={param.min}
            max={param.max}
            step={param.step ?? 0.1}
            value={param.value ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : parseFloat(e.target.value);
              onChange(val);
            }}
            className="w-16 px-2 py-1 text-xs border border-gray-200 rounded text-center"
          />
        </div>
      );
    }

    // Just number input for larger ranges
    return (
      <div className="flex items-center gap-3">
        <label className="w-24 text-xs text-gray-500 flex-shrink-0">
          {param.label}
        </label>
        <input
          type="number"
          min={param.min}
          max={param.max}
          step={param.step ?? 1}
          placeholder={language === 'zh' ? '留空=默认' : 'Empty=default'}
          value={param.value ?? ''}
          onChange={(e) => {
            const val = e.target.value === '' ? null : parseFloat(e.target.value);
            onChange(val);
          }}
          className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded"
        />
      </div>
    );
  }

  return null;
}

/** Shared price formatter */
function formatPrice(pricing: ModelInfo['pricing'], language: string) {
  if (!pricing || (pricing.input_per_million === null && pricing.output_per_million === null)) {
    return null;
  }

  if (language === 'zh') {
    const input = pricing.input_per_million !== null ? (pricing.input_per_million * USD_TO_RMB).toFixed(2) : '?';
    const output = pricing.output_per_million !== null ? (pricing.output_per_million * USD_TO_RMB).toFixed(2) : '?';
    return `¥${input} / ¥${output}`;
  }

  const input = pricing.input_per_million?.toFixed(2) ?? '?';
  const output = pricing.output_per_million?.toFixed(2) ?? '?';
  return `$${input} / $${output}`;
}

/** Round to nearest 0.05 (五分级别四舍五入) */
function roundToFiveCents(value: number): string {
  return (Math.round(value * 20) / 20).toFixed(2);
}

/** Estimated cost badge (always visible) */
function EstimatedCostBadge({ model, language }: { model: ModelInfo; language: string }) {
  if (!model.estimated_cost) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
      {language === 'zh'
        ? `≈¥${roundToFiveCents(model.estimated_cost.estimated_cost_rmb)}/轮`
        : `≈$${roundToFiveCents(model.estimated_cost.estimated_cost_usd)}/req`}
    </span>
  );
}

/**
 * Unified ModelSelector component.
 *
 * Three modes:
 * - Inline: force minimal row display (inline=true)
 * - Compact: when no linkedParameters, featureToggle, or sublabel
 * - Enhanced: when any of those props are present
 */
export function ModelSelector(props: EnhancedModelSelectorProps) {
  const {
    linkedParameters = [],
    featureToggle,
    sublabel,
    inline,
  } = props;

  // Force inline mode if explicitly requested
  if (inline) {
    return <InlineModelSelector {...props} />;
  }

  const isCompact = !linkedParameters.length && !featureToggle && !sublabel;

  if (isCompact) {
    return <CompactModelSelector {...props} />;
  }
  return <EnhancedModelSelectorInner {...props} linkedParameters={linkedParameters} />;
}

// Re-export as alias for backward compatibility
export { ModelSelector as EnhancedModelSelector };

// ─── Inline Mode ────────────────────────────────────────────────────────────

function InlineModelSelector({
  value,
  onChange,
  models,
  label,
  allowEmpty = false,
  emptyLabel = '-- Select --',
  disabled = false,
}: EnhancedModelSelectorProps) {
  const { language } = useI18n();
  const [modalOpen, setModalOpen] = useState(false);

  const selectedModel = models.find(m => m.id === value);

  const handleClick = () => {
    if (!disabled) {
      setModalOpen(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2
          bg-white border border-slate-200 rounded-md
          text-left text-sm transition-all
          ${disabled
            ? 'cursor-not-allowed opacity-50'
            : 'hover:border-slate-300 hover:bg-slate-50/50 cursor-pointer'
          }
        `}
      >
        {selectedModel ? (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="font-medium text-slate-800 truncate">
              {selectedModel.display_name}
            </span>
            {/* Capability badges - minimal */}
            {selectedModel.supports_reasoning && (
              <Sparkles className="w-3 h-3 text-purple-400 flex-shrink-0" />
            )}
            {selectedModel.supports_vision && (
              <Eye className="w-3 h-3 text-blue-400 flex-shrink-0" />
            )}
            {/* Provider + Price - subtle */}
            <span className="text-xs text-slate-400 flex-shrink-0 hidden sm:inline">
              {selectedModel.provider}
              {selectedModel.pricing && formatPrice(selectedModel.pricing, language) && (
                <> · {formatPrice(selectedModel.pricing, language)}</>
              )}
            </span>
            <EstimatedCostBadge model={selectedModel} language={language} />
          </div>
        ) : (
          <span className="text-slate-400">
            {allowEmpty ? (language === 'zh' ? '使用主模型' : 'Use main model') : emptyLabel}
          </span>
        )}
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </button>

      <ModelSelectorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        value={value}
        onChange={onChange}
        models={models}
        title={`${language === 'zh' ? '选择' : 'Select'} ${label}`}
        allowEmpty={allowEmpty}
        emptyLabel={emptyLabel}
      />
    </>
  );
}

// ─── Compact Mode ───────────────────────────────────────────────────────────

function CompactModelSelector({
  value,
  onChange,
  models,
  label,
  allowEmpty = false,
  emptyLabel = '-- Select --',
  disabled = false,
}: EnhancedModelSelectorProps) {
  const { language } = useI18n();
  const [modalOpen, setModalOpen] = useState(false);

  const selectedModel = models.find(m => m.id === value);

  const handleClick = () => {
    if (!disabled) {
      setModalOpen(true);
    }
  };

  return (
    <>
      {/* Clickable card */}
      <div
        onClick={handleClick}
        className={`
          group border rounded-lg p-3 transition-all
          ${disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer'
          }
        `}
      >
        {/* Header row: label + chevron */}
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-gray-600">
            {label}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-colors ${
              !disabled && 'group-hover:text-blue-500'
            }`}
          />
        </div>

        {/* Selected model display */}
        {selectedModel ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {/* Model name */}
              <span className="font-medium text-gray-900 truncate">
                {selectedModel.display_name}
              </span>
              {/* Capability badges */}
              {selectedModel.supports_reasoning && (
                <span title="Supports reasoning">
                  <Sparkles className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                </span>
              )}
              {selectedModel.supports_vision && (
                <span title="Supports vision">
                  <Eye className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                </span>
              )}
            </div>
            {/* Provider & pricing */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {selectedModel.provider && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  {selectedModel.provider}
                </span>
              )}
              {selectedModel.pricing && formatPrice(selectedModel.pricing, language) && (
                <span className="text-xs text-gray-400">
                  {formatPrice(selectedModel.pricing, language)}
                </span>
              )}
              <EstimatedCostBadge model={selectedModel} language={language} />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400 italic">
              {allowEmpty ? 'Disabled' : emptyLabel}
            </span>
          </div>
        )}
      </div>

      <ModelSelectorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        value={value}
        onChange={onChange}
        models={models}
        title={`Select ${label}`}
        allowEmpty={allowEmpty}
        emptyLabel={emptyLabel}
      />
    </>
  );
}

// ─── Enhanced Mode ──────────────────────────────────────────────────────────

function EnhancedModelSelectorInner({
  value,
  onChange,
  models,
  label,
  sublabel,
  allowEmpty = false,
  emptyLabel,
  disabled = false,
  linkedParameters = [],
  onParameterChange,
  isPrimary = false,
  featureToggle,
}: EnhancedModelSelectorProps & { linkedParameters: LinkedParameter[] }) {
  const { language } = useI18n();
  const [modalOpen, setModalOpen] = useState(false);
  const [paramsExpanded, setParamsExpanded] = useState(false);

  const selectedModel = models.find(m => m.id === value);
  const hasParameters = linkedParameters.length > 0;

  // Feature toggle state
  const hasFeatureToggle = !!featureToggle;
  const featureEnabled = featureToggle?.featureEnabled ?? true;
  const featureLabel = featureToggle?.featureLabel || label;

  const handleClick = () => {
    if (!disabled && featureEnabled) {
      setModalOpen(true);
    }
  };

  const handleFeatureToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && featureToggle?.onFeatureToggle) {
      featureToggle.onFeatureToggle(!featureEnabled);
    }
  };

  const handleParameterChange = (name: string, val: any) => {
    onParameterChange?.(name, val);
  };

  // Get capability icons and colors based on model features
  const getModelStyle = () => {
    if (!selectedModel) return { gradient: 'from-gray-100 to-gray-50', iconColor: 'text-gray-400' };

    if (selectedModel.supports_reasoning && selectedModel.supports_vision) {
      return { gradient: 'from-purple-50 via-blue-50 to-indigo-50', iconColor: 'text-purple-600' };
    }
    if (selectedModel.supports_reasoning) {
      return { gradient: 'from-purple-50 to-indigo-50', iconColor: 'text-purple-600' };
    }
    if (selectedModel.supports_vision) {
      return { gradient: 'from-blue-50 to-cyan-50', iconColor: 'text-blue-600' };
    }
    return { gradient: 'from-emerald-50 to-teal-50', iconColor: 'text-emerald-600' };
  };

  const style = getModelStyle();

  // Consistent min-height for all model selectors (always applied for layout consistency)
  const minHeight = 'min-h-[100px]';

  // Determine border color based on feature state
  const getBorderClass = () => {
    if (disabled) return 'border-gray-200 opacity-60';
    if (!featureEnabled) return 'border-gray-200';
    return 'border-gray-200 hover:border-gray-300 hover:shadow-sm';
  };

  return (
    <div className={`rounded-xl border transition-all ${getBorderClass()}`}>
      {/* Header row (always shown for consistent layout) */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          <Brain className={`w-4 h-4 ${featureEnabled ? style.iconColor : 'text-gray-400'}`} />
          <span className={`text-sm font-medium ${featureEnabled ? 'text-gray-700' : 'text-gray-500'}`}>
            {hasFeatureToggle ? featureLabel : label}
          </span>
        </div>
        {/* Feature toggle button (only for optional features) */}
        {hasFeatureToggle && (
          <button
            type="button"
            onClick={handleFeatureToggle}
            disabled={disabled}
            className={`
              flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all
              ${featureEnabled
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }
              ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            `}
          >
            {featureEnabled ? (
              <>
                <ToggleRight className="w-4 h-4" />
                <span>{language === 'zh' ? '已启用' : 'Enabled'}</span>
              </>
            ) : (
              <>
                <ToggleLeft className="w-4 h-4" />
                <span>{language === 'zh' ? '已禁用' : 'Disabled'}</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Main model display (always shown, with disabled styling when feature is off) */}
      <div
        onClick={handleClick}
        className={`
          relative overflow-hidden ${minHeight}
          ${!hasParameters ? 'rounded-b-xl' : ''}
          ${disabled || !featureEnabled ? 'cursor-not-allowed' : 'cursor-pointer'}
          bg-gradient-to-br ${selectedModel ? style.gradient : 'from-gray-100 to-gray-50'}
          transition-all
          ${!featureEnabled ? 'opacity-50' : ''}
        `}
      >
          {/* Decorative background pattern */}
          {selectedModel && isPrimary && (
            <div className="absolute inset-0 opacity-5">
              <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-current" />
              <div className="absolute -right-8 top-8 w-16 h-16 rounded-full bg-current" />
            </div>
          )}

          <div className="relative p-4">

            {/* Selected Model Display */}
            {selectedModel ? (
              <div className="space-y-1">
                {/* Model name */}
                <div className="flex items-center justify-between">
                  <span className={`text-lg font-semibold ${featureEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
                    {selectedModel.display_name}
                  </span>
                  {/* Capability indicators - subtle icons only */}
                  <div className="flex items-center gap-1">
                    {selectedModel.supports_reasoning && (
                      <span title={language === 'zh' ? '支持推理' : 'Supports reasoning'}>
                        <Sparkles className={`w-3.5 h-3.5 ${featureEnabled ? 'text-purple-400' : 'text-gray-400'}`} />
                      </span>
                    )}
                    {selectedModel.supports_vision && (
                      <span title={language === 'zh' ? '支持视觉' : 'Supports vision'}>
                        <Eye className={`w-3.5 h-3.5 ${featureEnabled ? 'text-blue-400' : 'text-gray-400'}`} />
                      </span>
                    )}
                  </div>
                </div>

                {/* Model details row - compact */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {selectedModel.provider && (
                    <span>{selectedModel.provider}</span>
                  )}
                  {selectedModel.max_input_tokens && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>{(selectedModel.max_input_tokens / 1000).toFixed(0)}K</span>
                    </>
                  )}
                  {selectedModel.pricing && formatPrice(selectedModel.pricing, language) && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-gray-400">
                        {formatPrice(selectedModel.pricing, language)}
                      </span>
                    </>
                  )}
                  <EstimatedCostBadge model={selectedModel} language={language} />
                </div>
              </div>
            ) : (
              <div className="py-2">
                <span className="text-gray-400 italic">
                  {emptyLabel || (language === 'zh' ? '点击选择模型' : 'Click to select')}
                </span>
              </div>
            )}

            {/* Sublabel */}
            {sublabel && (
              <p className="mt-2 text-xs text-gray-400">{sublabel}</p>
            )}
          </div>
        </div>

      {/* Parameters section (expandable) - show with disabled styling when feature is off */}
      {hasParameters && (
        <div className={`border-t border-gray-100 ${!featureEnabled ? 'opacity-50' : ''}`}>
          {/* Toggle button */}
          <button
            type="button"
            onClick={() => featureEnabled && setParamsExpanded(!paramsExpanded)}
            disabled={!featureEnabled}
            className={`
              w-full flex items-center justify-between px-4 py-2 text-sm text-gray-500 transition-colors rounded-b-xl
              ${featureEnabled ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-not-allowed'}
            `}
          >
            <div className="flex items-center gap-2">
              <Sliders className="w-3.5 h-3.5" />
              <span>{language === 'zh' ? '模型参数' : 'Parameters'}</span>
            </div>
            {paramsExpanded && featureEnabled ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {/* Parameter controls */}
          {paramsExpanded && featureEnabled && (
            <div className="px-4 pb-4 space-y-3 bg-gray-50/50 rounded-b-xl">
              {linkedParameters.map((param) => (
                <ParameterControl
                  key={param.name}
                  param={param}
                  onChange={(val) => handleParameterChange(param.name, val)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <ModelSelectorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        value={value}
        onChange={onChange}
        models={models}
        title={`${language === 'zh' ? '选择' : 'Select'} ${label}`}
        allowEmpty={allowEmpty}
        emptyLabel={emptyLabel}
      />
    </div>
  );
}
